import { useTranslation } from 'react-i18next';
import DatePicker from '../ui/DatePicker';
import { CurrencySelect } from '../ui/CurrencySelect';
import { Icon, CheckCircle2, ChevronDown, ChevronUp } from '../ui/Icon';
import type { ExpenseCategory, PaymentMethod } from '../../types';
import { EXPENSE_CATEGORIES } from './expenseDefaults';
import { rowIsValid, type ScannedRow } from './scannedExpense';

export default function ScannedExpenseRow({ row, expanded, onToggleChecked, onToggleExpanded, onChange, defaultMonth, converted, baseCurrency, rateMissing }: {
  row: ScannedRow;
  expanded: boolean;
  onToggleChecked: () => void;
  onToggleExpanded: () => void;
  onChange: (patch: Partial<ScannedRow>) => void;
  defaultMonth: string;
  /** The row's amount in the trip's base currency, once a rate is known. */
  converted: number | null;
  baseCurrency: string;
  rateMissing: boolean;
}) {
  const { t } = useTranslation();
  const invalid = row.checked && !rowIsValid(row);
  const subtitle = [
    row.date,
    t(`expenses.categories.${row.category}` as Parameters<typeof t>[0]),
    row.status === 'PENDING' ? t('expenses.status.PENDING') : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className={`rounded-xl border transition-colors ${
      invalid
        ? 'border-red-300 dark:border-red-800'
        : row.checked ? 'border-slate-200 dark:border-slate-700' : 'border-slate-100 dark:border-slate-800'
    }`}>
      <div className="flex items-center gap-2 px-2.5 py-2">
        <button
          type="button"
          onClick={onToggleChecked}
          aria-label={t(row.checked ? 'expenses.scan.exclude' : 'expenses.scan.include')}
          aria-pressed={row.checked}
          className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors cursor-pointer ${
            row.checked ? 'bg-teal-500 border-teal-500 text-white' : 'border-slate-300 dark:border-slate-600'
          }`}
        >
          {row.checked ? <Icon as={CheckCircle2} size={13} /> : null}
        </button>
        <button type="button" onClick={onToggleExpanded} className="min-w-0 flex-1 flex items-center gap-2 text-left cursor-pointer">
          <span className="min-w-0 flex-1">
            <span className={`block text-sm truncate ${row.checked ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400 line-through'}`}>
              {row.description || t('expenses.scan.noDescription')}
            </span>
            <span className="block text-[11px] text-slate-400 truncate">{subtitle}</span>
          </span>
          <span className="shrink-0 text-right">
            <span className={`block text-sm font-semibold tabular-nums ${row.checked ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400'}`}>
              {row.amount || '—'} {row.currency}
            </span>
            {row.currency !== baseCurrency && (
              <span className="block text-[11px] text-slate-400 tabular-nums">
                {rateMissing ? t('expenses.scan.noRate') : converted != null ? `≈ ${converted.toFixed(2)} ${baseCurrency}` : ''}
              </span>
            )}
          </span>
          <Icon as={expanded ? ChevronUp : ChevronDown} size={15} className="text-slate-400 shrink-0" />
        </button>
      </div>

      {expanded && (
        <div className="px-2.5 pb-3 pt-1 space-y-3 border-t border-slate-100 dark:border-slate-800">
          <div>
            <label className="label">{t('expenses.form.description')} *</label>
            <input
              type="text"
              className={'input' + (row.checked && !row.description.trim() ? ' input-error' : '')}
              value={row.description}
              onChange={e => onChange({ description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="label">{t('expenses.form.amount')} *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                className={'input' + (row.checked && !(parseFloat(row.amount) > 0) ? ' input-error' : '')}
                value={row.amount}
                onChange={e => onChange({ amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="label">{t('expenses.form.currency')}</label>
              <CurrencySelect value={row.currency} onChange={v => onChange({ currency: v })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">{t('expenses.form.date')} *</label>
              <DatePicker
                value={row.date}
                onChange={v => onChange({ date: v })}
                defaultMonth={defaultMonth}
                error={row.checked && !row.date}
              />
            </div>
            <div>
              <label className="label">{t('expenses.form.category')}</label>
              <select className="input" value={row.category} onChange={e => onChange({ category: e.target.value as ExpenseCategory })}>
                {EXPENSE_CATEGORIES.map(c => (
                  <option key={c} value={c}>{t(`expenses.categories.${c}` as Parameters<typeof t>[0])}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">{t('expenses.form.payment')}</label>
              <select className="input" value={row.paymentMethod} onChange={e => onChange({ paymentMethod: e.target.value as PaymentMethod })}>
                <option value="CARD">{t('expenses.payment.CARD')}</option>
                <option value="CASH">{t('expenses.payment.CASH')}</option>
                <option value="TRANSFER">{t('expenses.payment.TRANSFER')}</option>
              </select>
            </div>
            <div>
              <label className="label">{t('expenses.form.status')}</label>
              <select className="input" value={row.status} onChange={e => onChange({ status: e.target.value as 'PAID' | 'PENDING' })}>
                <option value="PAID">{t('expenses.status.PAID')}</option>
                <option value="PENDING">{t('expenses.status.PENDING')}</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
